import { useMemo, useEffect, useState, useCallback, useReducer } from "react";
import { Container, Row } from "react-bootstrap";
import moment from "moment";
import { cloneDeep } from "lodash";
import { pdf } from "@react-pdf/renderer";

import { canAccess } from "utils/authorize-action";
import { showErrorAlert } from "utils/alert";
import { useAppContext } from "context/appContext";
import { DATE_TIME_FORMATS } from "constant";
import {
    ReportFilterItem,
    MonthTimeRange,
    ReportSummaryInfo,
    ReportTimeRangeType,
    ReportTabType,
    ReportItemStateData,
    ReportItemsState,
    ReportItemMap,
    ReportOverviewData,
    ReportPDFSummaryInfo,
    ReportPDFOverviewData,
    ReportPDFFilterItems,
    ReportPDFIndividualData,
    ReportItemsLoading,
    ReportItemType,
} from "types/Reports";
import { REPORT_SENSOR_REGISTRY } from "constant/report";
import { useResourceStore } from "store/useResourceStore";

import {
    getReportOrganizationName,
    isDisableReportBtnAction,
    getShowTabs,
} from "components/report/utils/report";
import { initializeReportPage } from "components/report/utils/reportInitialize";
import {
    transformReportDataToPDFSummaryInfo,
    transformReportDataToPDFOverviewData,
    transformReportDataToPDFIndividualData,
} from "components/report/utils/reportTransform";
import {
    initialReportItemsLoading,
    initialReportItemsState,
    initialReportItemsStateData,
    initialReportOverviewData,
    initialSummaryInfo,
} from "components/report/utils/reportInitData";
import {
    buildLoadingPlaceholderItemsData,
    convertFilterItemsToIds,
    convertReportItemsStateToPDFFilterItems,
    convertReportItemsStateToReportFilterItemsState,
} from "components/report/utils/reportConvert";
import { retrieveReportPageData } from "components/report/utils/reportRetrieve";
import { getResourceString } from "components/report/utils/reportString";
import { getCategoryOfMonth } from "components/report/utils/reportGenerate";

import ReportHeader from "components/report/ReportHeader";
import ReportFilters from "components/report/ReportFilters";
import ReportOverviewTab from "components/report/ReportOverviewTab";
import ReportTab from "components/report/ReportTab";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import ReportTabItem from "components/report/ReportTabItem";
import ReportPDF from "./ReportPDF";

import "./styles.css";

type ReportDataState = {
    itemsData: ReportItemStateData;
    overviewData: ReportOverviewData;
    summaryInfo: ReportSummaryInfo;
    isItemsLoading: ReportItemsLoading;
};

type ReportDataAction =
    | { type: 'SET_ITEMS_DATA'; payload: React.SetStateAction<ReportItemStateData> }
    | { type: 'SET_OVERVIEW_DATA'; payload: React.SetStateAction<ReportOverviewData> }
    | { type: 'SET_SUMMARY_INFO'; payload: React.SetStateAction<ReportSummaryInfo> }
    | { type: 'SET_ITEMS_LOADING'; payload: { itemType: ReportItemType; value: boolean } }
    | { type: 'RESET_ITEMS_LOADING'; payload: ReportItemsLoading };

const initialNotLoadingState: ReportItemsLoading = {
    room: false,
    energy: false,
    water: false,
    waterLeakage: false,
    temperature: false,
    humidity: false,
    co2: false,
    pressure: false,
    tvoc: false,
    pm1: false,
    'pm2.5': false,
    pm10: false,
};

const initialReportDataState: ReportDataState = {
    itemsData: initialReportItemsStateData,
    overviewData: initialReportOverviewData,
    summaryInfo: cloneDeep(initialSummaryInfo),
    isItemsLoading: initialNotLoadingState,
};

const reportDataReducer = (state: ReportDataState, action: ReportDataAction): ReportDataState => {
    switch (action.type) {
        case 'SET_ITEMS_DATA': {
            const next = typeof action.payload === 'function'
                ? action.payload(state.itemsData) : action.payload;
            return { ...state, itemsData: next };
        }
        case 'SET_OVERVIEW_DATA': {
            const next = typeof action.payload === 'function'
                ? action.payload(state.overviewData) : action.payload;
            return { ...state, overviewData: next };
        }
        case 'SET_SUMMARY_INFO': {
            const next = typeof action.payload === 'function'
                ? action.payload(state.summaryInfo) : action.payload;
            return { ...state, summaryInfo: next };
        }
        case 'SET_ITEMS_LOADING':
            return {
                ...state,
                isItemsLoading: { ...state.isItemsLoading, [action.payload.itemType]: action.payload.value },
            };
        case 'RESET_ITEMS_LOADING':
            return { ...state, isItemsLoading: action.payload };
        default:
            return state;
    }
};

const Reports = () => {
    const dateFormat = DATE_TIME_FORMATS.DATE_ONLY;

    const [isResourcesLoading, setIsResourcesLoading] = useState<boolean>(true);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [initialized, setInitialized] = useState(false);

    // Batched report data state
    const [reportData, dispatchReportData] = useReducer(reportDataReducer, initialReportDataState);
    const { itemsData, overviewData, summaryInfo, isItemsLoading } = reportData;

    // Wrapper setters to maintain existing interface for reportRetrieve/reportInitialize
    const setItemsData: React.Dispatch<React.SetStateAction<ReportItemStateData>> = useCallback(
        (value) => dispatchReportData({ type: 'SET_ITEMS_DATA', payload: value }),
        [],
    );
    const setOverviewData: React.Dispatch<React.SetStateAction<ReportOverviewData>> = useCallback(
        (value) => dispatchReportData({ type: 'SET_OVERVIEW_DATA', payload: value }),
        [],
    );
    const setSummaryInfo: React.Dispatch<React.SetStateAction<ReportSummaryInfo>> = useCallback(
        (value) => dispatchReportData({ type: 'SET_SUMMARY_INFO', payload: value }),
        [],
    );

    // Lists of data
    const [items, setItems] = useState<ReportItemsState>(initialReportItemsState);

    const [timeRange, setTimeRange] = useState<MonthTimeRange>({
        startDate: moment().startOf('month'),
        endDate: moment(),
    });
    const [appliedTimeRange, setAppliedTimeRange] = useState<MonthTimeRange>({
        startDate: moment().startOf('month'),
        endDate: moment(),
    });
    const [timeRangeType, setTimeRangeType] = useState<ReportTimeRangeType>('all');
    const [appliedTimeRangeType, setAppliedTimeRangeType] = useState<ReportTimeRangeType>('all');
    const [appliedCategories, setAppliedCategories] = useState<string[]>([]);

    // Display flags
    const { disableExport: isDisableExport, disableApply: isDisableApply } = useMemo(
        () => isDisableReportBtnAction(items, isItemsLoading),
        [items, isItemsLoading],
    );
    const [viewTab, setViewTab] = useState<ReportTabType>('overview');
    const [showTabs, setShowTabs] = useState<ReportTabType[]>([]);

    const {
        storeData: {
            organization: { currentOrgId, currentOrgInfo },
            systemPermissions: { permissions },
        },
    } = useAppContext();

    const { isResourcesLoaded } = useResourceStore();

    const hasPermission = useMemo(
        () => canAccess("dashboard:read") && !isForbiddenResource,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [permissions, isForbiddenResource]
    );

    const setSelectedItems = useCallback(<K extends keyof ReportItemMap>(
        key: K,
        selectedIds: string[]
    ) => {
        setItems(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                selectedIds,
            }
        }));
    }, []);

    const setItemsLoading = useCallback((type: ReportItemType, value: boolean) => {
        dispatchReportData({ type: 'SET_ITEMS_LOADING', payload: { itemType: type, value } });
    }, []);

    useEffect(() => {
        if (initialized || !isResourcesLoaded || !currentOrgId) {
            return;
        }

        const fetch = async () => {
            setInitialized(true);

            // initialization data of the report page
            await initializeReportPage(
                currentOrgId,
                {
                    setResourcesLoading: setIsResourcesLoading,
                    setItems,
                    setShowTabs,
                },
            );
        };

        setIsResourcesLoading(true);

        fetch().catch((error) => {
            console.error('Failed to initialize the Report page', error);
            showErrorAlert({ message: 'Failed to initialize the report' });
            setInitialized(false);
        }).finally(() => {
            setIsResourcesLoading(false);
        });
    }, [currentOrgId, timeRange, initialized, isResourcesLoaded]);

    useEffect(() => {
        const checkPermission = () => {
            if (!hasPermission) {
                setIsResourcesLoading(false);
                setIsForbiddenResource(true);
                return;
            }

            setIsForbiddenResource(false);
        };

        checkPermission();
    }, [hasPermission]);

    const handleChangeSelectedItem = useCallback((key: keyof ReportItemMap, newList: ReportFilterItem[]) => {
        setSelectedItems(key, convertFilterItemsToIds(newList))
    }, [setSelectedItems]);

    const handleApply = useCallback(async () => {
        const applyChanges = async () => {
            const endDayOfMonth: moment.Moment = timeRange.endDate.endOf('month');
            const categories = getCategoryOfMonth(endDayOfMonth);

            const newShowTabs = getShowTabs(items, false);

            setAppliedCategories(categories);
            setAppliedTimeRangeType(timeRangeType);
            setItems((prev) => {
                const next = cloneDeep(prev);
                const types = (Object.keys(prev) as (keyof ReportItemsState)[]);

                for (const type of types) {
                    next[type].appliedIds = [...next[type].selectedIds];
                }

                return next;
            });
            setAppliedTimeRange({
                startDate: moment(timeRange.startDate),
                endDate: moment(timeRange.endDate),
            });
            setShowTabs(newShowTabs);
            setViewTab('overview');
            // Seed one placeholder ChartSeries per applied device so every applied
            // card renders immediately with its loading spinner. Without this the
            // section/list maps over the previous Apply's chartSeries and newly
            // added items have no card to attach `isLoading` to.
            setItemsData(buildLoadingPlaceholderItemsData(items));
            dispatchReportData({ type: 'RESET_ITEMS_LOADING', payload: cloneDeep(initialReportItemsLoading) });

            await retrieveReportPageData(
                items,
                timeRange,
                currentOrgId,
                endDayOfMonth,
                false,
                {
                    setItemsLoading,
                    setItemsData,
                    setSummaryInfo,
                    setOverviewData,
                },
            );

            // Signal the PDF-refresh effect: it'll rebuild the five pdf* values from
            // the now-current state on the next render.
            setApplyCount((prev) => prev + 1);
        };

        applyChanges().catch((error) => {
            console.error('Failed to apply changes the Report page', error);
            showErrorAlert({ message: 'Failed to apply changes' });
        });
    }, [items, timeRange, timeRangeType, currentOrgId, setItemsLoading, setItemsData, setOverviewData, setSummaryInfo]);
    const handleTimeRangeChange = useCallback(({ startDate, endDate }: MonthTimeRange) => {
        setTimeRange({
            startDate,
            endDate,
        });
    }, []);
    const handleTimeRangeTypeChange = useCallback((value: ReportTimeRangeType) => {
        setTimeRangeType(value);
    }, []);
    // PDF payloads are stored as state and refreshed only when the user clicks Apply
    // (see the effect below). This keeps them decoupled from live filter selections
    // and avoids re-running the transforms on every selectedIds change.
    const [pdfResourceString, setPdfResourceString] = useState<string>('');
    const [pdfSummaryInfo, setPdfSummaryInfo] = useState<ReportPDFSummaryInfo>(
        () => transformReportDataToPDFSummaryInfo('all', cloneDeep(initialSummaryInfo), initialReportItemsState),
    );
    const [pdfFilterItems, setPdfFilterItems] = useState<ReportPDFFilterItems>(
        () => convertReportItemsStateToPDFFilterItems(initialReportItemsState),
    );
    const [pdfOverviewInfo, setPdfOverviewInfo] = useState<ReportPDFOverviewData>(
        () => transformReportDataToPDFOverviewData(initialReportItemsState, initialReportOverviewData, 'all', cloneDeep(initialSummaryInfo)),
    );
    const [pdfIndividualInfo, setPdfIndividualInfo] = useState<ReportPDFIndividualData>(
        () => transformReportDataToPDFIndividualData(initialReportItemsStateData, 'all', []),
    );
    const [applyCount, setApplyCount] = useState(0);

    // After Apply resolves, `applyCount` bumps and this effect runs on the re-render
    // that carries the post-apply state — so closure sees the latest data. Running in
    // an effect (not directly in handleApply) avoids stale-closure reads of `summaryInfo`,
    // `overviewData`, and `itemsData`, which `retrieveReportPageData` populates via setters.
    useEffect(() => {
        if (applyCount === 0) return;

        setPdfResourceString(getResourceString(items));
        setPdfSummaryInfo(transformReportDataToPDFSummaryInfo(appliedTimeRangeType, summaryInfo, items));
        setPdfFilterItems(convertReportItemsStateToPDFFilterItems(items));
        setPdfOverviewInfo(transformReportDataToPDFOverviewData(items, overviewData, timeRangeType, summaryInfo));
        setPdfIndividualInfo(transformReportDataToPDFIndividualData(itemsData, timeRangeType, appliedCategories));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyCount]);

    const handleExportPDF = useCallback(async () => {
        // handle the extremely long name. I don't use the ReactPDF css because it has strange behaviors with different words.
        const limitLengthOrganizationName = (name: string) => {
            if (name.length > 32) {
                return name.substring(0, 32) + '...';
            }

            return name;
        };

        const blob = await pdf(
            <ReportPDF
                organizationName={limitLengthOrganizationName(getReportOrganizationName(currentOrgInfo))}
                reportPeriod={appliedTimeRange.startDate.format(DATE_TIME_FORMATS.MONTH_ONLY)}
                timeRangeType={timeRangeType}
                resource={pdfResourceString}

                summaryInfo={pdfSummaryInfo}
                overviewInfo={pdfOverviewInfo}
                individualInfo={pdfIndividualInfo}
                filterItems={pdfFilterItems}
            />
        ).toBlob();

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'report.pdf';
        link.click();

        URL.revokeObjectURL(link.href);
    }, [currentOrgInfo, appliedTimeRange, timeRangeType, pdfResourceString, pdfSummaryInfo, pdfOverviewInfo, pdfIndividualInfo, pdfFilterItems]);
    const handleSwitchTab = useCallback((tab: ReportTabType) => {
        setViewTab(tab);
    }, []);

    const organizationName = useMemo(
        () => getReportOrganizationName(currentOrgInfo),
        [currentOrgInfo],
    );

    const filterItems = useMemo(
        () => convertReportItemsStateToReportFilterItemsState(items),
        [items],
    );

    const roomSections = useMemo(() => [{
        title: '',
        titleHidden: true,
        chartSeries: itemsData.room.chartSeries,
        prevChartSeries: itemsData.room.prevChartSeries,
        itemType: 'room' as const,
        isLoading: isItemsLoading.room,
        highlightClassName: REPORT_SENSOR_REGISTRY.room.colorClassName,
        color: REPORT_SENSOR_REGISTRY.room.colorHex,
    }], [itemsData.room, isItemsLoading.room]);

    const utilitiesSections = useMemo(() => [
        {
            title: 'Energy Usage',
            titleHidden: false,
            chartSeries: itemsData.energy.chartSeries,
            prevChartSeries: itemsData.energy.prevChartSeries,
            itemType: 'energy' as const,
            isLoading: isItemsLoading.energy,
            highlightClassName: REPORT_SENSOR_REGISTRY.energy.colorClassName,
            color: REPORT_SENSOR_REGISTRY.energy.colorHex,
        },
        {
            title: 'Water Usage',
            titleHidden: false,
            chartSeries: itemsData.water.chartSeries,
            prevChartSeries: itemsData.water.prevChartSeries,
            itemType: 'water' as const,
            isLoading: isItemsLoading.water,
            highlightClassName: REPORT_SENSOR_REGISTRY.water.colorClassName,
            color: REPORT_SENSOR_REGISTRY.water.colorHex,
        },
        {
            title: 'Water Leakage Detection',
            titleHidden: false,
            chartSeries: itemsData.waterLeakage.chartSeries,
            prevChartSeries: itemsData.waterLeakage.prevChartSeries,
            itemType: 'waterLeakage' as const,
            isLoading: isItemsLoading.waterLeakage,
            highlightClassName: REPORT_SENSOR_REGISTRY.waterLeakage.colorClassName,
            color: REPORT_SENSOR_REGISTRY.waterLeakage.colorHex,
        },
    ], [itemsData.energy, itemsData.water, itemsData.waterLeakage,
        isItemsLoading.energy, isItemsLoading.water, isItemsLoading.waterLeakage]);

    const environmentSections = useMemo(() => [
        {
            title: 'Temperature',
            titleHidden: false,
            chartSeries: itemsData.temperature.chartSeries,
            prevChartSeries: itemsData.temperature.prevChartSeries,
            itemType: 'temperature' as const,
            isLoading: isItemsLoading.temperature,
            highlightClassName: REPORT_SENSOR_REGISTRY.temperature.colorClassName,
            color: REPORT_SENSOR_REGISTRY.temperature.colorHex,
        },
        {
            title: 'Humidity',
            titleHidden: false,
            chartSeries: itemsData.humidity.chartSeries,
            prevChartSeries: itemsData.humidity.prevChartSeries,
            itemType: 'humidity' as const,
            isLoading: isItemsLoading.humidity,
            highlightClassName: REPORT_SENSOR_REGISTRY.humidity.colorClassName,
            color: REPORT_SENSOR_REGISTRY.humidity.colorHex,
        },
        {
            title: 'CO2',
            titleHidden: false,
            chartSeries: itemsData.co2.chartSeries,
            prevChartSeries: itemsData.co2.prevChartSeries,
            itemType: 'co2' as const,
            isLoading: isItemsLoading.co2,
            highlightClassName: REPORT_SENSOR_REGISTRY.co2.colorClassName,
            color: REPORT_SENSOR_REGISTRY.co2.colorHex,
        },
        {
            title: 'Pressure',
            titleHidden: false,
            chartSeries: itemsData.pressure.chartSeries,
            prevChartSeries: itemsData.pressure.prevChartSeries,
            itemType: 'pressure' as const,
            isLoading: isItemsLoading.pressure,
            highlightClassName: REPORT_SENSOR_REGISTRY.pressure.colorClassName,
            color: REPORT_SENSOR_REGISTRY.pressure.colorHex,
        },
    ], [itemsData.temperature, itemsData.humidity, itemsData.co2, itemsData.pressure,
        isItemsLoading.temperature, isItemsLoading.humidity, isItemsLoading.co2, isItemsLoading.pressure]);

    const airQualitySections = useMemo(() => [
        {
            title: 'TVOC',
            titleHidden: false,
            chartSeries: itemsData.tvoc.chartSeries,
            prevChartSeries: itemsData.tvoc.prevChartSeries,
            itemType: 'tvoc' as const,
            isLoading: isItemsLoading.tvoc,
            highlightClassName: REPORT_SENSOR_REGISTRY.tvoc.colorClassName,
            color: REPORT_SENSOR_REGISTRY.tvoc.colorHex,
        },
        {
            title: 'PM1.0',
            titleHidden: false,
            chartSeries: itemsData.pm1.chartSeries,
            prevChartSeries: itemsData.pm1.prevChartSeries,
            itemType: 'pm1' as const,
            isLoading: isItemsLoading.pm1,
            highlightClassName: REPORT_SENSOR_REGISTRY.pm1.colorClassName,
            color: REPORT_SENSOR_REGISTRY.pm1.colorHex,
        },
        {
            title: 'PM2.5',
            titleHidden: false,
            chartSeries: itemsData["pm2.5"].chartSeries,
            prevChartSeries: itemsData["pm2.5"].prevChartSeries,
            itemType: 'pm2.5' as const,
            isLoading: isItemsLoading["pm2.5"],
            highlightClassName: REPORT_SENSOR_REGISTRY["pm2.5"].colorClassName,
            color: REPORT_SENSOR_REGISTRY["pm2.5"].colorHex,
        },
        {
            title: 'PM10',
            titleHidden: false,
            chartSeries: itemsData.pm10.chartSeries,
            prevChartSeries: itemsData.pm10.prevChartSeries,
            itemType: 'pm10' as const,
            isLoading: isItemsLoading.pm10,
            highlightClassName: REPORT_SENSOR_REGISTRY.pm10.colorClassName,
            color: REPORT_SENSOR_REGISTRY.pm10.colorHex,
        },
    ], [itemsData, isItemsLoading]);

    return (
        <ContentWrapper
            isForbiddenResource={isForbiddenResource}
            isLoading={isResourcesLoading}
            title="Reports"
        >
            <div className="page-content report-page">
                <Container fluid>
                    <Row>
                        <ReportHeader
                            organizationName={organizationName}
                            isDisableExport={isDisableExport}
                            handleExportPDF={handleExportPDF}
                        />

                        <ReportFilters
                            items={filterItems}
                            handleItemChanged={handleChangeSelectedItem}

                            timeRange={timeRange}
                            handleTimeRangeChange={handleTimeRangeChange}

                            timeRangeType={timeRangeType}
                            handleTimeRangeTypeChange={handleTimeRangeTypeChange}

                            isDisableApply={isDisableApply}
                            handleApply={handleApply}
                        />

                        <ReportTab
                            showTabs={showTabs}
                            activeTab={viewTab}
                            handleSwitchTab={handleSwitchTab}
                        />

                        { viewTab === 'overview' ? <ReportOverviewTab
                            summaryInfo={summaryInfo}
                            timeRangeType={appliedTimeRangeType}
                            timeRange={appliedTimeRange}

                            numberOfRooms={items.room.appliedIds.length}
                            numberOfEnergy={items.energy.appliedIds.length}
                            numberOfWater={items.water.appliedIds.length}
                            numberOfWaterLeakage={items.waterLeakage.appliedIds.length}

                            isRoomLoading={isItemsLoading.room}
                            isEnergyLoading={isItemsLoading.energy}
                            isWaterLoading={isItemsLoading.water}
                            isWaterLeakageLoading={isItemsLoading.waterLeakage}

                            overviewData={overviewData}

                            dateFormat={dateFormat}
                        /> : <></> }

                        { viewTab === 'room' ? <ReportTabItem
                            sections={roomSections}
                            timeRangeType={appliedTimeRangeType}
                            categories={appliedCategories}
                            time={appliedTimeRange.endDate}
                            dateFormat={dateFormat}
                        /> : <></>}

                        { viewTab === 'utilities' ? <ReportTabItem
                            sections={utilitiesSections}
                            timeRangeType={appliedTimeRangeType}
                            categories={appliedCategories}
                            time={appliedTimeRange.endDate}
                            dateFormat={dateFormat}
                        /> : <></>}

                        { viewTab === 'environment' ? <ReportTabItem
                            sections={environmentSections}
                            timeRangeType={appliedTimeRangeType}
                            categories={appliedCategories}
                            time={appliedTimeRange.endDate}
                            dateFormat={dateFormat}
                        /> : <></>}

                        { viewTab === 'air_quality' ? <ReportTabItem
                            sections={airQualitySections}
                            timeRangeType={appliedTimeRangeType}
                            categories={appliedCategories}
                            time={appliedTimeRange.endDate}
                            dateFormat={dateFormat}
                        /> : <></>}

                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default Reports;
